import { CircleNotchIcon } from '@phosphor-icons/react/CircleNotch'
import { CopyIcon } from '@phosphor-icons/react/Copy'
import { MagnifyingGlassIcon } from '@phosphor-icons/react/MagnifyingGlass'
import { PlusIcon } from '@phosphor-icons/react/Plus'
import { StopIcon } from '@phosphor-icons/react/Stop'
import { TerminalWindowIcon } from '@phosphor-icons/react/TerminalWindow'
import { TrashIcon } from '@phosphor-icons/react/Trash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { codexApi, type TerminalOutput, type TerminalSession } from '../../api'
import type { StudioProject } from '../../storage'

type Props = {
  project: StudioProject
  taskId: string | null
  connection: 'checking' | 'ready' | 'offline'
  eventTransport: 'connecting' | 'connected' | 'reconnecting'
  events: TerminalOutput[]
}

const EMPTY_OUTPUT = 'Terminal output will appear here. Start a command to begin.'

export function TerminalView({ project, taskId, connection, eventTransport, events }: Props) {
  const [sessions, setSessions] = useState<TerminalSession[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [outputs, setOutputs] = useState<Record<string, string>>({})
  const [command, setCommand] = useState('')
  const [stdin, setStdin] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState(32)
  const [cols, setCols] = useState(120)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [stoppingId, setStoppingId] = useState<string | null>(null)
  const [newOutput, setNewOutput] = useState(false)
  const [error, setError] = useState('')
  const seenSequences = useRef(new Map<string, number>())
  const loadGeneration = useRef(0)
  const outputRef = useRef<HTMLPreElement>(null)
  const outputNearBottomRef = useRef(true)

  const load = async () => {
    const generation = ++loadGeneration.current
    if (!taskId || connection !== 'ready') { setSessions([]); setSelectedId(null); setOutputs({}); seenSequences.current.clear(); return }
    setLoading(true)
    setError('')
    try {
      const { terminals } = await codexApi.terminals(taskId)
      const loaded = terminals ?? []
      const initial = await Promise.all(loaded.map(async (session) => {
        try {
          const current = await codexApi.terminalOutput(session.id)
          return { session: current.session, output: current.output }
        } catch { return { session, output: '' } }
      }))
      if (generation !== loadGeneration.current) return
      const reconciled = initial.map((item) => item.session)
      setSessions(reconciled)
      setSelectedId((current) => current && reconciled.some((session) => session.id === current) ? current : reconciled.at(-1)?.id ?? null)
      setOutputs(Object.fromEntries(initial.map((item) => [item.session.id, item.output])))
      seenSequences.current.clear()
      for (const item of initial) seenSequences.current.set(item.session.id, item.session.sequence ?? 0)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Terminals could not be loaded.')
    } finally { if (generation === loadGeneration.current) setLoading(false) }
  }

  useEffect(() => { void load() }, [connection, eventTransport, taskId])

  useEffect(() => {
    for (const event of events.filter((item) => item.taskId === taskId && item.sessionId)) {
      const sequence = event.sequence ?? 0
      const previous = seenSequences.current.get(event.sessionId) ?? 0
      if (event.stream && event.text && sequence > previous) {
        seenSequences.current.set(event.sessionId, sequence)
        setOutputs((current) => ({ ...current, [event.sessionId]: `${current[event.sessionId] ?? ''}${event.text}`.slice(-65_536) }))
      }
      if (event.status) setSessions((current) => current.map((session) => session.id === event.sessionId ? { ...session, status: event.status as TerminalSession['status'], exitCode: event.exitCode ?? session.exitCode } : session))
    }
  }, [events, taskId])

  const selected = sessions.find((session) => session.id === selectedId) ?? null
  const visibleOutput = useMemo(() => {
    const raw = outputs[selected?.id ?? ''] ?? ''
    if (!search.trim()) return raw
    return raw.split('\n').filter((line) => line.toLocaleLowerCase().includes(search.toLocaleLowerCase())).join('\n')
  }, [outputs, search, selected?.id])

  useEffect(() => {
    const element = outputRef.current
    if (!element) return
    if (outputNearBottomRef.current) {
      element.scrollTo({ top: element.scrollHeight, behavior: 'auto' })
      setNewOutput(false)
    } else {
      setNewOutput(true)
    }
  }, [visibleOutput])

  const handleOutputScroll = () => {
    const element = outputRef.current
    if (!element) return
    outputNearBottomRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48
    if (outputNearBottomRef.current) setNewOutput(false)
  }

  const create = async () => {
    if (!taskId || !command.trim() || connection !== 'ready') return
    if (creating) return
    setCreating(true)
    setError('')
    try {
      const created = await codexApi.createTerminal({ taskId, cwd: project.cwd, command: command.trim(), tty: true })
      let session = created.session
      let output = ''
      try {
        const reconciled = await codexApi.terminalOutput(session.id)
        session = reconciled.session
        output = reconciled.output
      } catch { /* Live events remain the fallback when the immediate snapshot is unavailable. */ }
      setSessions((current) => [...current.filter((item) => item.id !== session.id), session])
      setSelectedId(session.id)
      setOutputs((current) => ({ ...current, [session.id]: output }))
      seenSequences.current.set(session.id, session.sequence ?? 0)
      setCommand('')
    } catch (createError) { setError(createError instanceof Error ? createError.message : 'Terminal could not be started.') }
    finally { setCreating(false) }
  }

  const write = async (closeStdin = false) => {
    if (!selected || (!stdin && !closeStdin)) return
    try { await codexApi.writeTerminal(selected.id, stdin, closeStdin); setStdin('') } catch (writeError) { setError(writeError instanceof Error ? writeError.message : 'Terminal input could not be sent.') }
  }

  const stop = async () => {
    if (!selected) return
    if (stoppingId === selected.id) return
    setStoppingId(selected.id)
    try {
      const { terminal } = await codexApi.stopTerminal(selected.id)
      let reconciled = terminal
      let output = outputs[selected.id] ?? ''
      try {
        const snapshot = await codexApi.terminalOutput(selected.id)
        reconciled = snapshot.session
        output = snapshot.output
      } catch { /* The stop response remains authoritative if the snapshot races process exit. */ }
      setSessions((current) => current.map((session) => session.id === reconciled.id ? reconciled : session))
      setOutputs((current) => ({ ...current, [reconciled.id]: output }))
      seenSequences.current.set(reconciled.id, reconciled.sequence ?? seenSequences.current.get(reconciled.id) ?? 0)
    } catch (stopError) { setError(stopError instanceof Error ? stopError.message : 'Terminal could not be stopped.') }
    finally { setStoppingId(null) }
  }

  const clear = async () => {
    if (!selected) return
    try {
      const { terminal } = await codexApi.clearTerminal(selected.id)
      setSessions((current) => current.map((session) => session.id === terminal.id ? terminal : session))
      setOutputs((current) => ({ ...current, [selected.id]: '' }))
      seenSequences.current.set(selected.id, terminal.sequence ?? seenSequences.current.get(selected.id) ?? 0)
    } catch (clearError) { setError(clearError instanceof Error ? clearError.message : 'Terminal could not be cleared.') }
  }

  const resize = async () => {
    if (!selected) return
    try { await codexApi.resizeTerminal(selected.id, rows, cols) } catch (resizeError) { setError(resizeError instanceof Error ? resizeError.message : 'Terminal could not be resized.') }
  }

  if (!taskId) return <section className="terminal-view empty-panel"><TerminalWindowIcon size={30} /><h2>Integrated terminal</h2><p>Start or select a task to open a project-scoped terminal.</p></section>

  return <section className="terminal-view" aria-label="Integrated terminal">
    <header className="terminal-header">
      <div><span className="content-kicker">PROJECT TERMINAL</span><h2><TerminalWindowIcon /> {project.name}</h2><p>{project.cwd}</p></div>
      <button type="button" className="terminal-refresh" onClick={() => void load()} disabled={loading}>{loading ? <CircleNotchIcon className="spin" /> : 'Refresh'}</button>
    </header>
    {eventTransport === 'reconnecting' && <div className="terminal-connection-status" role="status"><CircleNotchIcon className="spin" /> Reconnecting to the local terminal bridge…</div>}
    <div className="terminal-toolbar">
      <input value={command} onChange={(event) => setCommand(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void create() } }} placeholder="Run a command (for example: npm test)" aria-label="New terminal command" />
      <button type="button" onClick={() => void create()} disabled={creating || !command.trim() || connection !== 'ready'}>{creating ? <CircleNotchIcon className="spin" /> : <PlusIcon />} {creating ? 'Starting…' : 'New terminal'}</button>
    </div>
    {error && <div className="terminal-error" role="alert">{error}</div>}
    <div className="terminal-tabs" role="tablist" aria-label="Terminal sessions">
      {sessions.map((session, index) => <button key={session.id} type="button" role="tab" aria-selected={session.id === selectedId} className={session.id === selectedId ? 'is-active' : ''} onClick={() => setSelectedId(session.id)}><span>{Array.isArray(session.command) ? session.command.at(-1) : session.command}</span><small>{session.status === 'running' || session.status === 'starting' ? '●' : session.exitCode ?? '×'}</small></button>)}
      {!sessions.length && <span className="terminal-empty-tabs">No terminal sessions yet</span>}
    </div>
    <div className="terminal-panel">
      <div className="terminal-panel-toolbar">
        <label><MagnifyingGlassIcon /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search output" aria-label="Search terminal output" /></label>
        <span className="terminal-spacer" />
        <label className="terminal-size">Rows <input type="number" min="1" max="500" value={rows} onChange={(event) => setRows(Number(event.target.value))} /></label>
        <label className="terminal-size">Cols <input type="number" min="1" max="500" value={cols} onChange={(event) => setCols(Number(event.target.value))} /></label>
        <button type="button" onClick={() => void resize()} disabled={!selected}>Resize</button>
        <button type="button" onClick={() => void navigator.clipboard?.writeText(outputs[selected?.id ?? ''] ?? '')} disabled={!selected}><CopyIcon /> Copy</button>
        <button type="button" onClick={() => void clear()} disabled={!selected}><TrashIcon /> Clear</button>
        <button type="button" className="is-danger" onClick={() => void stop()} disabled={!selected || stoppingId === selected.id || !['running', 'starting'].includes(selected.status)}>{stoppingId === selected?.id ? <CircleNotchIcon className="spin" /> : <StopIcon />} {stoppingId === selected?.id ? 'Stopping…' : 'Stop'}</button>
      </div>
      <pre ref={outputRef} className="terminal-output" onScroll={handleOutputScroll}>{selected ? (visibleOutput || EMPTY_OUTPUT) : 'Choose a terminal session or start a new one above.'}</pre>
      {newOutput && <button type="button" className="terminal-new-output" onClick={() => { outputNearBottomRef.current = true; outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight, behavior: 'smooth' }); setNewOutput(false) }}>New output ↓</button>}
      <form className="terminal-stdin" onSubmit={(event) => { event.preventDefault(); void write() }}>
        <span>›</span><input value={stdin} onChange={(event) => setStdin(event.target.value)} onKeyDown={(event) => { if (event.ctrlKey && event.key.toLowerCase() === 'c' && selected) { event.preventDefault(); void codexApi.writeTerminal(selected.id, '\u0003') } }} placeholder={selected ? 'Send input to the selected PTY (Ctrl+C interrupts)' : 'Select a terminal first'} disabled={!selected} /><button type="submit" disabled={!selected || !stdin}>Send</button><button type="button" onClick={() => void write(true)} disabled={!selected}>Close stdin</button>
      </form>
    </div>
    <p className="terminal-footnote">Commands run through the Codex App Server workspace sandbox. Output is kept to a bounded live buffer; large logs are retained locally as short-lived artifacts.</p>
  </section>
}
